/**
 * ████████╗ ██████╗ ██╗     ██╗███╗   ██╗ ██████╗
 * ╚══██╔══╝██╔═══██╗██║     ██║████╗  ██║██╔═══██╗
 *    ██║   ██║   ██║██║     ██║██╔██╗ ██║██║   ██║
 *    ██║   ██║   ██║██║     ██║██║╚██╗██║██║   ██║
 *    ██║   ╚██████╔╝███████╗██║██║ ╚████║╚██████╔╝
 *    ╚═╝    ╚═════╝ ╚══════╝╚═╝╚═╝  ╚═══╝ ╚═════╝
 *
 * (c) Copyright 2021-present Rakuten Kobo Inc. (https://www.kobo.com)
 */

// -----------------------------------------------------------------------------
// IMPORTS
// -----------------------------------------------------------------------------

// External/Third-party dependencies
import QueryString from 'query-string';


// -----------------------------------------------------------------------------
// CONFIGURATION
// -----------------------------------------------------------------------------

// Tag for log output etc.
const TAG = '[service/legacy-helper]';


// -----------------------------------------------------------------------------
// LEGACY HELPER SERVICE
// -----------------------------------------------------------------------------

function createLegacyHelperService(logger) {

  /**
   * Transforms old/legacy URLs that were used to invoke an app into
   * new/current URL to invoke an app.
   *
   * In older version, i.e. below 6.0.0 of our tolino apps, routes, parameters
   * etc. were passed to applications via the URL hash. Starting with v6.0.0,
   * these parameters are passed to an application via "normal" URL
   * parameters. This function helps to transform old URLs to new ones.
   *
   * @param {string} url The old/legacy URL that was used to invoke an app
   * @return {URL} The converted URL
   */
  function transformLegacyInvocation(url) {
    url = new URL(url);
    // We need to transform parameters that are part of the hash into "proper"
    // URL parameters
    if (url?.hash) {
      logger.warn(`${TAG} Legacy URL with URL hash parameters detected -> transforming`);
      const params = QueryString.parse(url.hash.split('?')[1]);
      if (params) {
        const newURL = new URL(`${url.origin}${url.pathname}?${QueryString.stringify(params)}`);
        logger.warn(`${TAG} Legacy URL converted: ${url} -> ${newURL}`);
        return newURL;
      }
    }

    return null;
  }


  /**
   * Transforms old/deprecated/legacy URL parameter names to new names
   *
   * @param {object} params Provided parameters
   * @param {object} legacyParams Mapping of old to new parameter nams
   * @return {object} Converted parameters with new names
   */
  function transformLegacyURLParams(params, legacyParams) {
    // If no params are provided, we will do nothing
    if (!params || Object.keys(params).length === 0) {
      logger.warn(`${TAG} No URL parameter names provided -> Skipping transformation`);
      return { };
    }

    // We don't have any information on how to transform parameters, so we
    // return the original list of params
    if (!legacyParams || Object.keys(legacyParams).length === 0) {
      logger.warn(`${TAG} No legacy URL parameter name mapping provided -> Skipping transformation`);
      return params;
    }

    Object.keys(params).forEach(paramName => {
      if (legacyParams[paramName.toLocaleLowerCase()]) {
        const legacyConvertedParamName = legacyParams[paramName.toLocaleLowerCase()];
        params[legacyConvertedParamName] = params[paramName];
        delete params[paramName];

        logger.warn(`${TAG} URL parameter ${paramName} is deprecated -> Converted to ${legacyConvertedParamName}`);
      }
    });

    return params;
  }


  // ---------------------------------------------------------------------------
  // PUBLIC API
  // ---------------------------------------------------------------------------

  return {
    transformLegacyInvocation,
    transformLegacyURLParams
  };
}


// -----------------------------------------------------------------------------
// SERVICE REGISTRATION
// -----------------------------------------------------------------------------

//service config for service locator
export const config = ["legacyHelper", [ "logger" ], createLegacyHelperService];

// -----------------------------------------------------------------------------
// EXPORTS
// -----------------------------------------------------------------------------

export default createLegacyHelperService;
